import React from 'react';
import { connect } from '../../../../redux/store';
import {
  ErrorMessage,
  EmptyMessage,
  LoadingMessage,
  StaticTypeahead,
  C,
} from '@kineticdata/bundle-common';
import { I18n } from '@kineticdata/react';
import { Markdown } from '../Markdown';
import { DefaultActions } from './';
import { CONTENT_MANAGER_MODE } from '../helpers';

const RateCardSuggestionContent = ({ suggestion }) => (
  <>
    <div className="small">
      {[
        suggestion.get('category'),
        suggestion.get('family'),
        suggestion.get('productName'),
      ]
        .filter(Boolean)
        .join(' - ')}
    </div>
    <div className="large">{suggestion.get('code')}</div>
  </>
);

const mapStateToProps = (state, props) => {
  const isPublicMode = props.mode === CONTENT_MANAGER_MODE.PUBLIC;
  const isViewMode = props.mode !== CONTENT_MANAGER_MODE.EDIT;
  const id = props.activeItem && props.activeItem.id;
  const rateCodes = props.values.get(C.FIELD_RATE_CODE) || [];
  const rateCards = state.app.catalog.rateCards;
  const relatedRateCards = rateCards.filter(rateCard =>
    rateCodes.includes(rateCard.values[C.FIELD_RATE_CODE]),
  );

  return {
    isPublicMode,
    isViewMode,
    id,
    rateCards,
    relatedRateCards,
    rateCardsLoading: !state.app.catalog.init && state.app.catalog.loading,
    rateCardsError: state.app.catalog.errors.first(),
  };
};

const ServiceCostsRenderer = connect(mapStateToProps)(
  ({
    mode,
    isPublicMode,
    isViewMode,
    content,
    values,
    activeItem,
    id,
    rateCards,
    relatedRateCards,
    rateCardsLoading,
    rateCardsError,
    imageDir,
    ...props
  }) => {
    return !!activeItem ? (
      <>
        <h2>
          <I18n>{activeItem.title}</I18n>
        </h2>

        <div className="form mb-3">
          {!isViewMode && (
            <div className="form-group">
              <label htmlFor="title">
                <I18n>Section Title</I18n>
              </label>
              <input
                type="text"
                id="title"
                value={activeItem.title}
                onChange={props.handleContentChange(id, 'title')}
              />
            </div>
          )}

          <div className="form-group">
            {!isViewMode && (
              <label htmlFor="content">
                <I18n>Section Content</I18n>
              </label>
            )}
            <Markdown
              initialValue={activeItem.value}
              onChange={props.handleContentChangeDirect(id, 'value')}
              imageDir={imageDir}
              disabled={isViewMode}
            />
          </div>

          {!isViewMode && (
            <div className="form-group">
              <label htmlFor="service-rate-codes" className="field-label">
                <I18n>Rate Codes</I18n>
              </label>
              <StaticTypeahead
                valueProp="code"
                labelProp="code"
                options={rateCards.toJS().map(p => ({
                  code: p.values[C.FIELD_RATE_CODE],
                  family: p.values['Family'],
                  productName: p.values['Product Name'],
                  category: p.values['Category'],
                  productCode: p.values['Product Code'],
                }))}
                value={values
                  .get(C.FIELD_RATE_CODE, [])
                  .map(code => ({ code }))}
                onChange={vals =>
                  props.handleValuesChangeDirect(id, C.FIELD_RATE_CODE)(
                    vals.toJS().map(rc => rc.code),
                  )
                }
                search={{
                  fields: [
                    { name: 'code', operator: 'matches' },
                    { name: 'category', operator: 'matches' },
                    { name: 'family', operator: 'matches' },
                    { name: 'productName', operator: 'matches' },
                    { name: 'productCode', operator: 'matches' },
                  ],
                }}
                multiple={true}
                minSearchLength={1}
                messages={{
                  short:
                    'Find rate codes by code, category, family, or product name.',
                }}
                components={{
                  SuggestionContent: RateCardSuggestionContent,
                }}
                id="service-rate-codes"
              />
            </div>
          )}

          {rateCardsError ? (
            <ErrorMessage
              title="The rate cards failed to load."
              message={rateCardsError.message}
            />
          ) : rateCardsLoading ? (
            <LoadingMessage />
          ) : relatedRateCards.size === 0 ? (
            <EmptyMessage title="There are no rate cards related to this service." />
          ) : (
            <table className="table table--settings">
              <thead>
                <tr>
                  <th>
                    <I18n>Rate Code</I18n>
                  </th>
                  <th>
                    <I18n>Category</I18n>
                  </th>
                  <th>
                    <I18n>Family</I18n>
                  </th>
                  <th>
                    <I18n>Product Name</I18n>
                  </th>
                  <th>
                    <I18n>List Price</I18n>
                  </th>
                  <th>
                    <I18n>Unit of Measure</I18n>
                  </th>
                </tr>
              </thead>
              <tbody>
                {relatedRateCards.map(rateCard => (
                  <tr key={rateCard.id}>
                    <td>{rateCard.values[C.FIELD_RATE_CODE]}</td>
                    <td>{rateCard.values['Family']}</td>
                    <td>{rateCard.values['Product Name']}</td>
                    <td>{rateCard.values['Category']}</td>
                    <td>{rateCard.values['List Price']}</td>
                    <td>{rateCard.values['Unit of Measure']}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!isViewMode && (
          <DefaultActions
            id={id}
            isDirty={activeItem.isDirty}
            handleResetSection={props.handleResetSection}
            handleContentDelete={props.handleContentDelete}
            actionsPending={props.actionsPending}
          />
        )}
      </>
    ) : null;
  },
);

// Metadata for this renderer
ServiceCostsRenderer.ID = 'SERVICE_COSTS';
ServiceCostsRenderer.NAME = 'Service Costs';
ServiceCostsRenderer.TYPES = ['Service'];

// Define fields managed by this section so we can know which fields to reset
// when the section is reset. Can store additional metadata for fields to use in
// the renderer above.
ServiceCostsRenderer.FIELDS = {
  [C.FIELD_RATE_CODE]: {},
};

export default ServiceCostsRenderer;
